import { useContextDays } from '@rehookify/datepicker';
import styled from 'styled-components';

import { formatDate } from '../../../common/helpers/formatDate.helper';
import { IconCalendar } from '../../../common/icons/cdl/Calendar';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { Caption, CaptionProps } from '../../Caption/Caption';
import { InputLabel } from '../../Input/components/CoreInput';
import { InputContainer, InputContainerProps } from '../../Input/components/InputContainer';
import { InputLabelText } from '../../Input/components/InputLabelText';
import { LeadingVisualWrapper } from '../../Input/components/LeadingVisualWrapper';
import { Optional } from '../../Input/components/Optional';
import { Required } from '../../Input/components/Required';
import { Text } from '../../Text/Text';
import { getDateInputBoxShadow, getDateInputHoverBoxShadow } from '../helpers/getDateInputStyles';

const DateInputContainer = styled(InputContainer)<InputContainerProps>`
    cursor: pointer;
    box-shadow: ${(props) => getDateInputBoxShadow(props)};

    &:hover {
        box-shadow: ${(props) => getDateInputHoverBoxShadow(props)};
    }
`;

interface CustomDateInputProps extends InputContainerProps {
    label?: string;
    markAsRequired?: boolean;
    markAsOptional?: boolean;
    description?: string;
    placeholder?: string;
    caption?: string;
    CaptionIcon?: CaptionProps['Icon'];
}

export const CustomDateInput = ({
    label,
    markAsRequired,
    markAsOptional,
    description,
    placeholder,
    caption,
    CaptionIcon,
    ...rest
}: CustomDateInputProps) => {
    const { selectedDates } = useContextDays();

    return (
        <InputLabel>
            <Box display="flex" flexDirection="column" gap={4}>
                {label ? (
                    <InputLabelText>
                        {label}
                        {markAsRequired ? <Required /> : null}
                        {markAsOptional ? <Optional /> : null}
                    </InputLabelText>
                ) : null}

                {description ? (
                    <Text variant="extraSmall" color="foreground.muted">
                        {description}
                    </Text>
                ) : null}

                <DateInputContainer {...rest}>
                    <LeadingVisualWrapper>
                        <IconCalendar width={16} height={16} color={theme.colors.foreground.muted} />
                    </LeadingVisualWrapper>
                    <Box
                        padding="6px 10px"
                        style={{ cursor: 'pointer' }}
                        color={selectedDates[0] ? 'foreground.default' : 'foreground.muted'}
                    >
                        {selectedDates[0] ? formatDate({ date: selectedDates[0].getTime() }) : placeholder ?? null}
                    </Box>
                </DateInputContainer>

                {caption ? (
                    <Caption $negative={rest.$negative} Icon={CaptionIcon}>
                        {caption}
                    </Caption>
                ) : null}
            </Box>
        </InputLabel>
    );
};
